<template>
  <div class='send-message' @click.stop=''>
    <div class='send-message-content'
          v-if='!sendMessageOutcome && !sending'>
      <button
        type='button'
        class='close-button'
        @click="$emit('close-modal')">
        <sword-icon-wuk name='close'></sword-icon-wuk>
      </button>
      <message-form @submit-form='setMessageDataAndSendMessage'
                    :default-recipient='defaultRecipient'
                    :patients-list='patientsList'
                    :has-editable-recipient="hasEditableRecipient"
                    @cancel="$emit('close-modal')"
                    @reset-suggestions="$emit('reset-suggestions')"
                    @fetch-patients="(recipientName) => $emit('fetch-patients', recipientName)">
      </message-form>
    </div>

    <div v-else-if='!!sendMessageOutcome' class='send-message-feedback'>
      <div class='feedback-container'
            v-if='sendMessageOutcome === MESSAGE_STATUS.SUCCESS'>
        <div class='feedback-image-wrapper'>
          <img :src="require('@ui-kit/assets/images/feedback/message-sent.svg')"
                alt='message sent'/>
          <p class='t1 mt10'>
            {{ $t('FEEDBACK.YOUR_MESSAGE_WAS_SUCCESSFULLY_SENT') }}
          </p>
        </div>
      </div>

      <div class='feedback-container'
        v-else-if='sendMessageOutcome === MESSAGE_STATUS.ERROR'>
        <div class='feedback-image-wrapper'>
          <img :src="require('@ui-kit/assets/images/feedback/message-fail.svg')"
                alt='message failed' />
          <p class='t1 mt10'>
            {{ $t('FEEDBACK.YOUR_MESSAGE_WAS_NOT_SENT1') }}
          </p>
        </div>
        <div class='form-buttons'>
          <sword-button-wuk theme='secondary'
                            :aria-label="$t('COMMON.CANCEL')"
                            text='COMMON.CANCEL'
                            icon='close'
                            side='left'>
          </sword-button-wuk>
          <sword-button-wuk text='copy_5'
                            :aria-label="$t('copy_5')"
                            icon='retry'
                            @click.native='sendMessage'>
          </sword-button-wuk>
        </div>
      </div>
    </div>
    <div class='message-spinner-container' v-show='sending'>
      <img class='loading-spinner'
            :src="require('@ui-kit/assets/images/spinners/loader.svg')"
            alt='sending...' />
      <p class='t3 mb20 center-text'>
        {{ $t('copy_2071') }}
      </p>
    </div>
  </div>
</template>

<script>
import SwordButtonWuk from '@ui-kit/components/buttons/SwordButton.vue';
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';
import MessageForm from '@ui-kit/components/dt-messages/MessageForm.vue';

const MESSAGE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error'
};

export default {
  name: 'sendMessage',
  components: {
    SwordButtonWuk,
    SwordIconWuk,
    MessageForm,
  },
  props: {
    sending: {
      type: Boolean,
      default: false,
    },
    sendMessageOutcome: {
      type: String
    },
    defaultRecipient: {
      type: Object,
    },
    patientsList: {
      type: Array,
      default: () => [],
    },
    hasEditableRecipient: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      messageData: null,
      MESSAGE_STATUS,
    };
  },
  methods: {
    setMessageDataAndSendMessage(messageData) {
      if (messageData) {
        this.messageData = messageData;
      }

      this.sendMessage();
    },
    sendMessage() {
      this.$emit('send-message', this.messageData);
      this.messageData = null;
    },
  },
  beforeDestroy() {
    this.$emit('reset-suggestions');
  },
};
</script>

<style lang='scss' scoped>
%overlap {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.message-spinner-container {
  @extend %flex-column-container;
  @extend %overlap;
  justify-content: flex-end;
  align-items: center;

  .loading-spinner {
    width: 3.125rem;
    height: 3.125rem;
    margin-bottom: 3.125rem;
    flex-grow: 1;
  }
}

.send-message {
  box-shadow: $default-shadow-wuk;
  background-color: $color-white;
  width: 23.75rem;
  height: 33.125rem;
  border-radius: 0.5rem;
  align-items: center;
  position: fixed;
  bottom: 2.5rem;
  right: 2.5rem;
  width: 24rem;
  overflow: hidden;

  ::v-deep hr {
    width: 100%;
    margin: 0.625rem auto;
    border: 0;
    border-top: 1px solid $context-color-7;
  }

  .close-button {
    display: none;
    float: right !important;
    width: 3.5rem;
    height: 3.5rem;
    background: $color-white;
    border-radius: 100%;
    border: 1px solid $context-color-6;

    > .svg-icon.svg-fill {
      width: 1rem;
      text-align: center;
      fill: $context-color-6;
    }

    &:hover {
      border-color: $context-color-4;

      > .svg-icon.svg-fill {
        fill: $context-color-4;
      }
    }
  }

  .send-message-feedback {
    height: 100%;

    .feedback-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
    }

    .feedback-image-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      margin: auto;
    }

    img {
      height: 7rem;
    }

    .form-buttons {
      display: flex;
      justify-content: space-between;
      padding: 1rem;
    }
  }

  ::v-deep input,
  ::v-deep textarea {
    border: none;
    background-color: transparent;
    padding: 0;
  }

  ::v-deep textarea {
    height: 100%;
    outline: none;
    resize: none;
  }
}

.send-message-content {
  height: 100%;
}

@media only screen and (max-width: $max-smartphone-size) {
  .send-message {
    border-radius: 0;
    flex: 1;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    padding: 0;

    .send-message-form  {
      height: calc(100% - 4.75rem);
    }

    .close-button {
      display: initial;
      align-self: flex-end;
      flex-grow: 1;
      flex-shrink: 0;
      margin: 1.25rem 1.25rem 0 0;
    }
  }
}
</style>
